import { type FormulaToken, type FunctionFormulaToken } from "./customFormulaParser";

export interface FunctionValidationRule {
  validateArguments: (functionToken: FunctionFormulaToken, argumentTokens: FormulaToken[][]) => string | undefined;
}

export interface FormulaFunctionDefinition {
  name: string;
  description: string;
  syntax: string;
  insertText: string;
}

const GRAND_TOTAL_FUNCTION_NAME = "GrandTotal";

const validateGrandTotalArguments: FunctionValidationRule["validateArguments"] = (functionToken, argumentTokens) => {
  if (argumentTokens.length !== 1) {
    return `"${functionToken.name}" requires exactly one field name as an argument`;
  }

  const argument = argumentTokens[0];
  const singleToken = argument?.[0];
  if (!singleToken || argument.length !== 1 || singleToken.kind !== "field") {
    return `"${functionToken.name}" argument must be a single field name (e.g., ${functionToken.name}("Field Name"))`;
  }

  return undefined;
};

const validateAbsoluteArguments: FunctionValidationRule["validateArguments"] = (functionToken, argumentTokens) => {
  if (argumentTokens.length !== 1 || !argumentTokens[0]?.length) {
    return `"${functionToken.name}" requires exactly one value as an argument`;
  }

  return undefined;
};

export const functionValidationRules: Record<string, FunctionValidationRule> = {
  grandtotal: {
    validateArguments: validateGrandTotalArguments,
  },
  abs: {
    validateArguments: validateAbsoluteArguments,
  },
};

const formulaFunctionDefinitions: FormulaFunctionDefinition[] = [
  {
    name: GRAND_TOTAL_FUNCTION_NAME,
    description: "Returns the grand total value of a field, ignoring any applied filters",
    syntax: 'GrandTotal("Column Name")',
    insertText: "GrandTotal()",
  },
  {
    name: "ABS",
    description: "Returns the absolute value, turning negative numbers into positive ones",
    syntax: 'ABS("Column Name")',
    insertText: "ABS()",
  },
];

export const predefinedCustomFormulaFunctions: string[] = formulaFunctionDefinitions.map((fn) => fn.name);

const functionDefinitionsWithoutGrandTotal = formulaFunctionDefinitions.filter(
  (fn) => fn.name !== GRAND_TOTAL_FUNCTION_NAME
);

export const getFormulaFunctionDefinitions = (allowGrandTotal: boolean): FormulaFunctionDefinition[] =>
  allowGrandTotal ? formulaFunctionDefinitions : functionDefinitionsWithoutGrandTotal;
